import _ from 'lodash'
import Gardener from 'scoreboard/components/gardener'
import * as AddTypes from 'scoreboard/components/add-types'
const { CHILD, SIBLING } = AddTypes
import datasetsPasteAllowedFactory from './datasets-paste-allowed-factory'
import { Transform } from 'stream'
import Tree from 'kalpa-tree'
import regexEscape from 'escape-string-regexp'
import Dataset from './model/dataset'
const { DATASET, FOLDER, VIEW } = Dataset
import unwrapModel from 'scoreboard/components/unwrap-model'

var DatasetsGardener = Gardener.extend({
	/**
	 * The gardener for Datasets.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 */
	initialize: function() {
		Gardener.prototype.initialize.apply(this, arguments)
	},

	interested: function({domainType}) {
		return domainType === Dataset.prototype.domainType
	},

	_treeOptions: function() {
		// can't just pass the tree in directly b/c it isn't defined yet
		let datasetsPasteAllowed = datasetsPasteAllowedFactory(() => this.tree)
		var treeOptions = Gardener.prototype._treeOptions.apply(this, arguments)
		treeOptions.droppable = datasetsPasteAllowed.bind(datasetsPasteAllowed)
		treeOptions.stream = treeOptions.stream.pipe(this._iconTransform())
		treeOptions.accessors = { icon: 'icon' }
		treeOptions.movable = _.constant(true)
		return treeOptions
	},

	_iconTransform: function() {
		var icons = new Transform({ objectMode: true })
		icons._transform = function (node, enc, next) {
			node.icon = Dataset.icon(node.type)
			this.push(node)
			next()
		}
		return icons
	},

	_renderTree: function() {
		Gardener.prototype._renderTree.apply(this, arguments)
		// if we clicked on "private datasets" or "shared datasets", don't change
		// the highlight in the tree
		this.tree._onSelect = function(evt, node, i, j, opt) {
			if (node.id < -1) {
				this.toggle(node)
			} else {
				Tree.prototype._onSelect.apply(this, arguments)
			}
		}
	},

	// adapted from kalpa-tree/index.js:search
	search: function(text) {
		if (_.isEmpty(text)) {
			// clear the search if no text is provided
			this.tree.filter(null)
			return
		}

		var re = new RegExp(regexEscape(text) || '', 'i')
		this.tree.filter((node) =>
			re.test(node.label) && node.visible !== false &&
			// #13805 don't show "Private Datasets" and "Shared Datasets"
			node.id > 0)
	},

	_bindEvents: function() {
		Gardener.prototype._bindEvents.apply(this, arguments)

		// after a domain object is moved, be sure update the .rootDatasetId
		this.tree.on('move', this.updateRootDatasetId.bind(this))
	},

	/**
	 * Based on the type of the selected item and the type of an item about
	 * to be newly created, returns whether the newly created item should be
	 * positioned as a CHILD or SIBLING

	 * @param relative
	 *            the tree node attributes representing the relative item
	 * @param newItem
	 *            the model of the new item being added
	 * @return CHILD or SIBLING or ROOT
	 */
	getAddType: function(relative, newItem) {
		relative = unwrapModel(relative)
		newItem = unwrapModel(newItem)
		// if adding under a top-level heading like "Private Datasets" or
		// "Shared Datasets"
		if (_.isNil(relative.type)) {
			return CHILD
		}

		// assume a valid relative was specified
		if (newItem.type === DATASET) {
			if (relative.type === FOLDER) {
				return CHILD
			} else {
				return SIBLING
			}
		}

		if (newItem.type === FOLDER) {
			// folders should normally be added as a sibling (#28850 the folder
			// logic matches the logic from new-folder-add-type.js)
			return SIBLING
		}
		// nothing can be added under a view
		if (relative.type === VIEW) {
			return SIBLING
		}
		// otherwise, add as a child
		return CHILD
	},

	/**
	 * Updates the rootDatasetId for the node that was moved and its descendants
	 */
	updateRootDatasetId: function(node) {
		let rootDatasetId = _.get(this.getRootDataset(node), 'id')
		this.updateDescendantRootDataset(node, rootDatasetId)
	},

	/**
	 * Finds the root dataset for the given node
	 * @returns {Object}
	 */
	getRootDataset: function(node) {
		while (!_.isNil(node) && node.type != DATASET) {
			node = this.tree.parent(node.id)
		}
		return node
	},

	/**
	 * Updates the rootDatasetId for the given node and its descendants
	 */
	updateDescendantRootDataset: function(node, rootDatasetId) {
		// #31834 when moving top-level dataset folders, don't get rid of the
		// rootDatasetId information for datasets and their children
		if (node.type === 'dataset') return

		node.rootDatasetId = rootDatasetId
		_.each(this.tree.children(node), child =>
			this.updateDescendantRootDataset(child, rootDatasetId))
	}

})

export default DatasetsGardener


DatasetsGardener.__filename__ = 'datasets/datasets-gardener.js'
